import cx from 'classnames';
import { useEffect, useState } from 'react';
import { Location } from 'react-router';
import { matchRoutePath } from '../../libs/router/router-helpers';
import { sanitizeRichHtml } from '../../libs/sanitizeContent';

export type TBanner = {
  id?: number;
  message: string;
  type: 'warn' | 'info' | 'notice';
  dismissable?: boolean;

  route?: string;
  routeKey?: string;
  routeValue?: string;
  expiresAt?: string | null;
};

type TBannerProps = {
  banners: TBanner[];
  location: Location;
  namedRoutes: Record<string, string>;
};

function shouldDisplayBanner(banner: TBanner, location: Location, namedRoutes: Record<string, string>) {
  if (!banner.message) {
    return false;
  }

  if (banner.expiresAt && new Date(banner.expiresAt) < new Date()) {
    return false;
  }

  const route = banner.route;
  const currentPath = location?.pathname;
  const path = route ? namedRoutes[route] : undefined;

  // If banner specifies a route, check if we're on that route
  if (banner.route) {
    if (!path || !currentPath) {
      return false;
    }

    const match = matchRoutePath({ path, end: false }, currentPath);
    if (!match) {
      return false;
    }

    // Check routeKey/routeValue if specified
    if (banner.routeKey && banner.routeValue) {
      if (!(banner.routeKey in match.params)) {
        return false;
      }
      const value = (match.params as Record<string, any>)[banner.routeKey];
      if (value !== banner.routeValue) {
        return false;
      }
    }
  }

  return true;
}

function getDismissKey(id: number) {
  return `rio:banner-${id}-dismissed`;
}

export default function SiteBanner(props: TBannerProps) {
  const { banners, location, namedRoutes } = props;
  const [dismissedIds, setDismissedIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (!banners || banners.length === 0) {
      return;
    }

    const dismissed = new Set<number>();
    for (const banner of banners) {
      if (banner.id != null) {
        try {
          if (localStorage.getItem(getDismissKey(banner.id)) === '1') {
            dismissed.add(banner.id);
          }
        } catch {
          // localStorage may be unavailable
        }
      }
    }

    if (dismissed.size > 0) {
      setDismissedIds(dismissed);
    }
  }, [banners]);

  if (!banners || banners.length === 0) {
    return null;
  }

  function handleDismiss(id: number) {
    try {
      localStorage.setItem(getDismissKey(id), '1');
    } catch {
      // localStorage may be unavailable
    }
    setDismissedIds((prev) => new Set(prev).add(id));
  }

  const bannerElements = [];
  for (const banner of banners) {
    const isDismissable = banner.id != null && banner.dismissable !== false;

    if (isDismissable && dismissedIds.has(banner.id!)) {
      continue;
    }

    if (!shouldDisplayBanner(banner, location, namedRoutes)) {
      continue;
    }

    bannerElements.push(
      <div key={banner.id ?? banner.message} className={cx('rio-site-banner', `rio-site-banner--${banner.type}`)}>
        <div
          className="rio-site-banner__content"
          dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(banner.message) }}
        />
        {isDismissable && (
          <button className="rio-site-banner__close" onClick={() => handleDismiss(banner.id!)} aria-label="Dismiss">
            <i className="fa fa-times" />
          </button>
        )}
      </div>
    );
  }

  if (bannerElements.length > 0) {
    return <>{bannerElements}</>;
  }

  return null;
}
